import React from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { useConfigValue } from '~/shared/utils/config.js';
import {
  ProductListingFilter as BaseProductListingFilter,
  ProductListingFilterStyle,
} from '~/techstyle-shared/react-components';

import {
  createGetSwatchIcon,
  getFilterStyle,
} from '../../utils/productFilters.js';

const ListingFilter = styled(BaseProductListingFilter)`
  padding: 2px 0;

  ${({ theme }) => theme.gridFilterStyles.listingFilterStyle}

  :last-child {
    box-shadow:
      0 0 0 0 ${({ theme }) => theme.colors.desktopProductListingFilter},
      0 -1px 0 0 ${({ theme }) => theme.colors.desktopProductListingFilter};
  }
`;

const circleIconStyle = css`
  &&& {
    border: solid 1px ${({ theme }) => theme.colors.palette.black};
    box-shadow: none;
    height: 22px;
    width: 22px;
    margin-right: 8px;
    display: block;

    ${({ colorSwatches, value, backgroundImage }) =>
      colorSwatches[value] == null &&
      !backgroundImage &&
      css`
        display: none;

        + * {
          margin: auto;
        }
      `};
  }
`;

const filterCountStyle = css`
  ${({ theme }) => theme.gridFilterStyles.filterCountStyle}
`;

const filterFieldLabelStyle = css`
  ${({ theme }) => theme.gridFilterStyles.filterFieldLabelStyle}
`;

const filterFieldButtonStyle = css`
  &&& {
    padding: 12px 8px;
  }

  svg {
    margin -5px 0;
    ${({ theme }) => theme.gridFilterStyles.filterFieldButtonSvgStyle}
  }
`;

const nestedHeadingStyle = css`
  line-height: 18px;
  padding-bottom: 8px;

  ${({ theme }) => theme.gridFilterStyles.nestedHeadingStyle}

  &&& + ul {
    padding-top: 0;
  }
`;

const nestedListStyle = css`
  padding-top: 16px;
`;

const listStyle = css`
  &&& {
    padding-bottom: 0;
    padding-top: 16px;
    padding-left: 12px;
  }
`;

const inputStyle = css`
  ${({ theme }) => theme.gridFilterStyles.inputStyle}
`;

const itemLabelStyle = css`
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: ${({ theme }) => theme.colors.palette.black};
  padding: 0;
`;

const inputCheckedStyle = css`
  ${({ theme }) => theme.gridFilterStyles.inputCheckedStyle}
`;

const checkmarkStyle = css`
  visibility: invisible;
`;

const checkmarkCheckedStyle = css`
  color: ${({ theme }) => theme.colors.palette.white};
`;

const itemStyle = css`
  &&& {
    padding: 0 0 20px;
  }
`;

const swatchItemStyle = css`
  position: relative;
  justify-content: center;
  width: 100%;
  padding: 0;
`;

const swatchInputCheckedStyle = css`
  background-color: ${({ theme }) => theme.colors.palette.black};
`;

const swatchInputStyle = css`
  margin: 0;
  height: 100%;
  width: 100%;
  border: none;
  border-radius: 16px;

  ${({ theme }) => theme.gridFilterStyles.swatchInputStyle}
`;

const swatchListStyle = css`
  &&& {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-row-gap: 8px;
    grid-column-gap: 8px;
    justify-content: space-between;
    padding: 16px 0 24px 12px;
  }
`;

const swatchLabelStyle = css`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  z-index: 1;
  padding: 0 4px;
  width: 100%;
  cursor: pointer;

  ${({ isChecked }) =>
    isChecked &&
    css`
      color: ${({ theme }) => theme.colors.palette.white};
    `}

  ${({ theme }) => theme.gridFilterStyles.swatchLabelStyle}
`;

const rootStyle = {
  position: 'absolute',
  inset: 0,
};

const swatchCheckmarkStyle = css`
  display: none;
`;

const gridListStyle = css`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  justify-content: space-between;
  grid-row-gap: 8px;
`;

const gridInputStyle = css`
  margin: 0;
  height: 100%;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.palette.white};

  ${({ theme }) => theme.gridFilterStyles.gridInputStyle}
`;

const gridInputCheckedStyle = css`
  background-color: ${({ theme }) => theme.colors.palette.black};
`;

const gridLabelStyle = css`
  position: relative;
  letter-spacing: normal;
  text-align: center;
  padding: 8px;

  ${({ theme }) => theme.gridFilterStyles.gridLabelStyle}

  ${({ isChecked }) =>
    isChecked &&
    css`
      color: ${({ theme }) => theme.colors.palette.white};
    `}
`;

const gridItemStyle = css`
  width: 100%;
  margin: 0;
`;

const gridCheckboxProps = {
  inputStyle: gridInputStyle,
  inputCheckedStyle: gridInputCheckedStyle,
  rootStyle,
};

const checkboxProps = {
  inputStyle,
  inputCheckedStyle,
  checkmarkStyle,
  checkmarkCheckedStyle,
};

const swatchCheckboxProps = {
  checkmarkStyle: swatchCheckmarkStyle,
  inputCheckedStyle: swatchInputCheckedStyle,
  inputStyle: swatchInputStyle,
  rootStyle,
};

function DesktopProductListingFilter(props) {
  const imageSwatchFilters = useConfigValue('public.imageSwatchFilters');
  const filterImageCdnUrl = useConfigValue('public.filterImageCdnUrl');
  const { filterField, colorSwatches } = props;
  const facetName = filterField?.field;

  const getSwatchIcon = createGetSwatchIcon({
    facetName,
    colorSwatches,
    imageSwatchFilters,
    filterImageCdnUrl,
    circleIconStyle,
    CircleIconComponent: BaseProductListingFilter.CircleIcon,
  });

  const filterStyle = getFilterStyle(
    facetName,
    imageSwatchFilters,
    ProductListingFilterStyle.SWATCH
  );

  return (
    <ListingFilter
      checkboxProps={checkboxProps}
      circleIconStyle={circleIconStyle}
      filterCountStyle={filterCountStyle}
      filterFieldButtonStyle={filterFieldButtonStyle}
      filterFieldLabelStyle={filterFieldLabelStyle}
      getSwatchIcon={getSwatchIcon}
      gridCheckboxProps={gridCheckboxProps}
      gridItemStyle={gridItemStyle}
      gridLabelStyle={gridLabelStyle}
      gridListStyle={gridListStyle}
      itemLabelStyle={itemLabelStyle}
      itemStyle={itemStyle}
      listStyle={listStyle}
      nestedHeadingStyle={nestedHeadingStyle}
      nestedListStyle={nestedListStyle}
      swatchCheckboxProps={swatchCheckboxProps}
      swatchItemStyle={swatchItemStyle}
      swatchLabelStyle={swatchLabelStyle}
      swatchListStyle={swatchListStyle}
      filterStyle={filterStyle}
      {...props}
    />
  );
}

DesktopProductListingFilter.propTypes = {
  colorSwatches: PropTypes.object,
  filterField: PropTypes.shape({
    field: PropTypes.string,
  }),
};

export default DesktopProductListingFilter;
